import { pick } from 'lodash';
import { Observable } from 'rxjs';

import { LocalStorageKeys } from 'v2/modules/shared/interfaces/localstorage.interface';

import { AbstractStorageDataStore } from './abstract.storage-data-store';
import {
  IState,
  MixedStorageStore,
  MixedStorageStoreHash,
  StateData,
} from './data-store.types';

//

export abstract class AbstractMixedStorageDataStore<
  D extends StateData,
  S extends IState<D>,
> extends AbstractStorageDataStore<D, S> {
  abstract readonly state :S;
  abstract readonly state$ :Observable<D>;

  abstract readonly storageKey :LocalStorageKeys;
  abstract readonly storeHash :MixedStorageStoreHash<D>;

  #storeListLocal ?:(keyof D)[] = [];
  #storeListSession ?:(keyof D)[] = [];

  constructor() {
    super();
  }

  init() {
    this.buildStoreLists();

    super.init();
  }

  /**
   * NOTE that derived classes MUST to call this method in their constructors
   * after calling `super()`.
   */
  buildStoreLists() {
    if (this.storeHash) {
      const _addPropToStoreList = (prop :keyof D, store :MixedStorageStore) => {
        switch (store) {
          case MixedStorageStore.LOCAL: {
            this.#storeListLocal.push(prop);
            break;
          }
          case MixedStorageStore.SESSION: {
            this.#storeListSession.push(prop);
            break;
          }
          case MixedStorageStore.NONE:
          default: {
            break;
          }
        }
      };

      const specified :(keyof D)[] = Object.keys(this.storeHash);

      for (const prop of specified) {
        if (prop === '*') {
          const defaultStore = this.storeHash['*'];

          if (defaultStore) {
            this.state.KEYS.filter(
              (_p :keyof D) => !specified.includes(_p),
            ).forEach((_prop :keyof D) => _addPropToStoreList(_prop, defaultStore));
          }
        } else {
          _addPropToStoreList(prop, this.storeHash[prop]);
        }
      }
    }
  }

  hasStoreListLocal() {
    return Array.isArray(this.#storeListLocal) && this.#storeListLocal.length > 0;
  }
  hasStoreListSession() {
    return (
      Array.isArray(this.#storeListSession) && this.#storeListSession.length > 0
    );
  }

  /** Override this method for custom parsing behavior. */
  readFromStorage() :Partial<D> {
    if (!this.storageKey) {
      console.warn(`Cannot read from unknown storage key.`);

      return;
    }

    return {
      ...JSON.parse(localStorage.getItem(this.storageKey)),
      ...JSON.parse(sessionStorage.getItem(this.storageKey)),
    };
  }
  /** Override this method for custom saving behavior. */
  writeToStorage() :void {
    if (!this.storageKey) {
      console.warn(`Cannot write to unknown storage key.`);

      return;
    }

    const state = this.getState();
    const local = this.hasStoreListLocal() ? pick(state, this.#storeListLocal) : {};
    const session = this.hasStoreListSession()
      ? pick(state, this.#storeListSession)
      : {};

    localStorage.setItem(this.storageKey, JSON.stringify(local));
    sessionStorage.setItem(this.storageKey, JSON.stringify(session));
  }
}
